import { Direction } from '@material-ui/core';
import { MutableRefObject } from 'react';
import {
  DraftFile,
  FileTags,
  InvestmentContributionsObject,
  MultiLanguage,
  OfferKpiObject,
  ProfimexOverview,
  ProjectPropertyDataObject,
  UpdateOperatorAdminInput,
} from './generated/graphql-types';

export type ButtonColor = 'default' | 'inherit' | 'primary' | 'secondary';

interface Person {
  fullName?: string;
  profileImageUrl?: string;
  phoneNumber?: string;
  email: string | string[];
  subjects?: string[];
}

export interface InvestorUser {
  id?: string;
  settings?: UserSettings;
  operatorId: string;
  description?: string;
  firstName?: string;
  lastName?: string;
  imgUrl?: string;
  email: string;
  documentPermissions?: MediaDocumentPermissions[];
  investmentDetails: Investment[];
  language?: string;
  // documents?: DocumentFile[];
  operatorDetails: Operator[];
  userLegalEntities?: UserLegalEntity[];
}

export interface Investor {
  id?: string;
  fullName: string;
  profileImageUrl?: string;
  phoneNumber?: string;
  email: string;
  companyName?: string;
  companyId?: string;
  address?: string;
  applicationSettings?: ApplicationSettings;
  documentPermissions?: MediaDocumentPermissions[];
  investments: Investment[];
  language?: string;
  termsInfo?: TermsInfo;
  // documents?: DocumentFile[];
  demoUser?: boolean;
  uniqueId?: string;
  updatedAt?: Date;
  defaultLanguage?: 'en' | 'heb';
}

export interface CreateOrUpdateInvestorUnpublished extends InvestorUnpublished {
  prevEmail?: string;
}

export interface OperatorAdmin extends Person {
  notifications?: Notification[];
  operatorDetails: Operator | OperatorUnpublished;
  termsInfo?: TermsInfo;
  operatorType: 'Operator' | 'OperatorUnpublished';
}

export interface InvestorUnpublished extends Investor {
  unpublished?: boolean;
  investments: InvestmentUnpublished[];
  deleted?: boolean;
}
export interface TermsInfo {
  termsAgreed: boolean;
  marketingAgreed: boolean;
  ipDetails?: any;
  time?: Date;
}

export interface ApplicationSettings {
  id?: string;
  metaData?: any;
  applicationLanguage?: string;
}

export interface MediaDocumentPermissions {
  id: string;
  email: string;
  permissions: DocumentPermission[];
}

export interface DocumentPermission {
  name?: string;
  granted: boolean;
}

//User viewer type
export enum ViewerEnum {
  OPERATOR = 'OPERATOR',
  INVESTOR = 'INVESTOR',
  VIEW_AS_INVESTOR = 'VIEW_AS_INVESTOR',
  UNKNOWN = 'UNKNOWN',
}

//Holds the options for the ratio button
export enum SelectedCampaignOptionEnum {
  Offering = 'offering',
  Email = 'email',
}

//holds the tagging option for the smart file tagging
export enum TagOption {
  CONTENT = 'content', //tag by OCR reading of the file's content
  FILE_NAME = 'fileName', //tag by the file's name
}

//Holds the card options for operator contacts information
export enum ContactsCardOption {
  VIEW = 'view',
  PASTE = 'paste',
  IMPORT = 'import',
}

export enum EntityDetailsEnum {
  operatorDetails = 'operatorDetails',
  projectDetails = 'projectDetails',
  documentsDetails = 'documentsDetails',
  investmentDetails = 'investmentDetails',
  investorDetails = 'investorDetails',
  userDetails = 'userDetails',
  updatesDetails = 'updatesDetails',
  fundDetails = 'fundDetails',
  offerDetails = 'offerDetails',
}

//Defines the totals for investments amounts on overview page
export interface OverviewTotals {
  totalContributions: number; //All contributions - active and past
  activeContributions: number; // Only active contributions
  totalDistributions: number; //All distributions
  totalCommitments: number; //All commitments
  totalRemainingCommitments: number; //All remaining commitments (commitments - contributions)
  totalRemainingCommitmentsConverted: number; //All remaining commitments (commitments - contributions) not converted to USD
  overviewCurrency: string; //Currency of the totals (either converted to USD or not)
  overviewCurrencySymbol: string; // currency symbol
  convertToUSD: boolean; //Whether the totals were converted to USD
  totalPrincipalReturns: number; //All principals return from overall distributions
  totalAccumulatedInterest: number; //All accumulated interest from overall distributions
  totalEquityDistributions: number; //All equity distributions from overall distributions
}

export interface Investment {
  id?: string;
  fee?: ProjectFee;
  investmentPercentage?: number;
  investmentAmount?: InvestmentAmount[];
  ref: InvestmentRef[];
  investedBy?: string;
  holdings?: InvestmentHoldingGraphQL[];
  manualHoldings?: InvestmentHoldingGraphQL[];
  // documents?: DocumentFile[];
  type: InvestmentTypeEnum;
  projectDetails?: Project;
  fundDetails?: Fund;
  operatorDetails?: Operator;
  entityDetails?: Project | Fund;
  entityType?: EntityType;
  currency?: string;
  commitment?: number;
  investedBy: string;
  location?: Address;
  percentage?: number;
  date?: Date;
  exitDate?: Date;
  period?: number;
  isContribution?: boolean;
  description?: MultiLanguage;
  email?: string[];
  phone?: string;
  netRoi?: Number;
  netIrr?: Number;
  netCoc?: Number;
  grossRoi?: Number;
  grossIrr?: Number;
  grossCoc?: Number;
  legalEntityId?: string;

  //todo nir this section start the version 2 type for investment - after migration old values should be removed
  version?: number;
  distributions?: ProjectPropertyDataObject[]; //type of ProjectPropertyDataObject
  contributions?: InvestmentContributionsObject[]; //type of InvestmentContributionsObject
  customizedStatistics?: KeyValueEntity[];
  kpis?: string[];
}

//Defines number field type that support customized string representation
export type NumberFieldType = {
  numValue: number; //number representation of the value
  textValue: string; //string representation of the value
};

export interface InvestmentGraphql {
  id?: string;
  description?: MultiLanguage;
  operatorId: string;
  type: InvestmentTypeEnum;
  investedBy: string;
  ref: InvestmentRef[];
  email: string[];
  amount?: number;
  commitment?: number;
  percentage?: number;
  date?: Date;
  exitDate?: Date;
  period?: number;
  isContribution?: boolean;
  investorDetails?: Investor[];
  projectDetails?: Project | FundProject;
  fundDetails?: Fund;
  operatorDetails?: Operator;
  holdings?: InvestmentHoldingGraphQL[];
  manualHoldings?: InvestmentHoldingGraphQL[];
  phone?: string;
  fee?: ProjectFee;
  manual?: any;
  secondaryEmail?: string;
  legalEntityId?: string;
  customizedStatistics?: KeyValueEntity[];
}

export enum InvestmentTypeEnum {
  standard = 'standard',
  balloon = 'balloon',
}

export class InvestmentRef {
  key: InvestmentRefEnum;
  value: string;
}

export enum InvestmentRefEnum {
  projectId = 'projectId',
  fundId = 'fundId',
}

export interface InvestmentHoldingGraphQL {
  date: Date;
  value: string;
}

export interface FundProject extends EntityStatus {
  fundId: string;
  operatorId?: string;
  name?: string;
  description?: MultiLanguage;
  category?: ProjectCategoryEnum;
  location?: Address;
  portfolio?: KeyValueEntity[];
  layout?: LayoutData[];
  // metadata?: DocumentFile[];
  // documents?: DocumentFile[];
  // gallery?: DocumentFile[];
  projectDetails?: Project[];
  clientId?: string;
  updatedAt?: Date;
  investmentClasses?: investmentClassesType[];
}

export interface InvestmentsEnd {
  date: Date;
}

export interface InvestmentTypesMeta {
  interestRate?: number;
}

export interface Holding {
  date: Date;
  distribution?: ActualProjectedValue;
  cashOnCash?: ActualProjectedValue;
  equity?: ActualProjectedValue;
  uniqueId?: string;
  readonly?: boolean;
}

export interface ActualProjectedValue {
  value: number;
  projectedValue?: number;
}

export interface ProjectFee {
  acquisitionFee?: AcquisitionFee;
  managementFee?: ManagementFee;
  promote?: Promote;
  catchUp?: CatchUp;
  highWatermark?: HighWatermark;
  hurdleRate?: HurdleRate;
}

export interface AcquisitionFee {
  value: number;
  show: boolean;
}

export interface ManagementFee {
  value: number;
  type: 'capitalInvested' | 'propertyValue' | 'monthlyGrowthIncome';
  additionString: string;
  show: boolean;
}

export interface Promote {
  steps: CatchUp[];
  show: boolean;
}

export interface CatchUp {
  value: number;
  internalRateOfReturn: number;
  show: boolean;
}

export interface HighWatermark {
  value: boolean;
  show: boolean;
}

export interface HurdleRate {
  value: number;
  show: boolean;
}

export interface InvestmentAmount {
  date: Date;
  value: number;
  uniqueId: string;
  percentage?: number;
}

export interface InvestedIn {
  type: 'Fund' | 'Project' | 'ProjectUnpublished' | 'FundUnpublished';
  organization: Fund | Project | ProjectUnpublished | FundUnpublished | FundProject;
}

//Defines currency and amount pair (Example 'USD': 10000)
export interface CurrencyAmount {
  currency: string;
  amount: number;
}

export interface Fund extends EntityStatus {
  id?: string;
  operatorId?: string;
  name?: string;
  description?: MultiLanguage;
  // metadata?: DocumentFile[];
  currency?: string;
  // documents?: DocumentFile[];
  // gallery?: DocumentFile[];
  properties?: ProjectProperty[];
  status?: EntityStatusEnum;
  updatesDetails?: UpdateDetails[];
  location?: Address;
  layout?: LayoutData[];
  projectDetails?: FundProject[];
  entityType?: 'Fund';
  updatedAt?: Date;
  portfolio?: KeyValueEntity[];
  investments?: Investment[];
  operator?: Operator;
  category?: ProjectCategoryEnum;
  investmentDetails?: InvestmentGraphql[];
  distributions?: ProjectProperty[];
  inceptionDate?: Date;
  closingDate?: Date;
  kpis?: string[];
  investmentClasses?: investmentClassesType[];
}

export type LatLng = {
  lat: string;
  lng: string;
};

export interface ShowOrHidePropWithValue {
  show: boolean;
  value: string | number | Date | boolean;
}

export interface TableUpdates {
  dataToShow: UpdatesColumn[];
  values?: UpdateDetails[];
}

export type UpdatesColumn = string | 'title' | 'reportDate' | 'progress';

export interface UpdateDto {
  description?: MultiLanguage;
  progress?: number;
  ref: UpdateRef;
  reportDate?: Date;
  title?: MultiLanguage;
  documents?: DocumentFile[];
  delete?: boolean;
  id?: string;
  customRequestFunction?: () => any;
}

export interface UpdateRef {
  key: UpdateRefEnum;
  value: string;
}

export enum UpdateRefEnum {
  operatorId = 'operatorId',
  projectId = 'projectId',
  fundId = 'fundId',
}

export interface DistributionElement {
  date: Date;
  annotation?: string;
  value: number;
  projectedValue?: number;
  id?: string;
  uniqueId?: string;
}

export interface RoiElement {
  date: Date;
  annotation?: string;
  value: number;
  projectedValue?: number;
  id?: string;
  uniqueId?: string;
}

export interface RoeElement {
  date: Date;
  annotation?: string;
  value: number;
  projectedValue?: number;
  id?: string;
  uniqueId?: string;
}

export interface AssetElement {
  date: Date;
  annotation?: string;
  value: number;
  projectedValue?: number;
}

export interface LoanElement {
  date: Date;
  annotation?: string;
  value: number;
  projectedValue?: number;
}

export interface NOI {
  date: Date;
  annotation?: string;
  value: number;
  projectedValue?: number;
}

export interface OccupancyElement {
  date: Date;
  annotation?: string;
  value: number;
  projectedValue?: number;
}

export interface RentElement {
  date: Date;
  annotation?: string;
  value: number;
  projectedValue?: number;
}

export interface AssetElement {
  date: Date;
  annotation?: string;
  value: number;
  projectedValue?: number;
}

export interface RoiElement {
  date: Date;
  annotation?: string;
  value: number;
  projectedValue?: number;
}

export type LanguageContext = {
  direction: Direction;
  language: string;
};

export enum NotificationEntityEnum {
  projectId = 'projectId',
  fundId = 'fundId',
  offeringId = 'offeringId',
  operatorId = 'operatorId',
  legalEntityId = 'legalEntityId',
  bankAccountId = 'bankAccountId',
  taxInfoId = 'taxInfoId',
  accreditationInfoId = 'accreditationInfoId',
  investorFile = 'investorFile',
  Project = 'Project',
  Fund = 'Fund',
  Offering = 'Offering',
}

/**
 * Maps the entity type to it's investment ref enum
 */
export const EntityTypeToInvestmentRefMapping = {
  Fund: InvestmentRefEnum.fundId,
  Project: InvestmentRefEnum.projectId,
};

export type Route = {
  path: string;
  name: string;
  component: React.FC;
  exact?: boolean;
};

export interface OperatorInvestor extends DateVersion {
  name?: string;
  description?: string;
  info?: string;
  email: string;
  operatorId: string;
  // documents?: DocumentFile[];
  investmentDetails?: Investment[];
  location?: Address;
  phone?: string;
  tag?: string[];
  userDetails?: InvestorUser;
}

//primary email as key , secondary emails array as values
export interface PrimaryAndSecondaryEmails {
  [key: string]: string[];
}

//id as key , email as value
export interface EmailsIdMapping {
  [key: string]: string;
}

/**
 * Defines the profimex overview data
 */
export interface profimexOverviewType extends ProfimexOverview {
  pendingData: ProfimexOverviewDataResult; //Whether Data was received from server
  legalEntityId?: string; //related user legal entity id
  currencySymbol?: string; //Symbol of currency
}

export interface OperatorInvestorGraphql extends DateVersion {
  name?: string;
  description?: string;
  info?: string;
  email: string;
  operatorId: string;
  investmentDetails?: InvestmentGraphql[];
  isFromViewAs?: boolean;
}
export interface Operator {
  id?: string;
  name?: string;
  profileImageUrl?: string;
  phoneNumber?: string;
  email?: string | string[];
  contacts?: OperatorContact[];
  operatorPackageLevel?: string;
  address?: string;
  notifications?: Notification[];
  websiteUrl?: string;
  mobilePhoneNumber?: string;
  faxNumber?: string;
  description?: MultiLanguage;
  updatesDetails?: UpdateDetails[];
  layout?: LayoutData[];
  collectionType?: 'Operator' | 'OperatorUnpublished';
  contactPerson?: string;
  updatedAt?: Date;
  // metadata?: DocumentFile[];
  investorDetails?: OperatorInvestor[];
  offerDetails?: Offering[];
  customRequestFunction?: () => any;
  overviewKpisCategories?: string[];
}

export interface OperatorContact {
  phone?: string;
  fax?: string;
  email?: string;
  name?: string;
  mobilePhoneNumber?: string;
  main?: boolean;
}

export type Address = {
  address?: string;
  longitude?: number;
  latitude?: number;
};

//todo nir how to not copy the types we have on the graphql-types
export interface investmentClassesType {
  allocationAmount?: number;
  currency?: string;
  distributionPeriod?: string;
  endDate?: Date;
  extensionOptionDate?: Date;
  lockCurrency?: boolean;
  name: string;
  percentage?: number;
  type?: InvestmentClassEnum;
  isCompounding?: boolean;
}

export interface Project {
  id?: string;
  name?: string;
  operatorId?: string;
  description?: MultiLanguage;
  TotalValue?: ShowOrHidePropWithValue;
  operatorEquity?: ShowOrHidePropWithValue;
  location?: Address;
  currency?: string;
  properties?: ProjectProperty[];
  minimumInvestmentAmount?: number;
  expectedCashOnCash?: number;
  investmentEquity?: number;
  status?: EntityStatusEnum;
  updatesDetails?: UpdateDetails[];
  layout?: LayoutData[];
  operator?: Operator;
  startDate?: Date;
  investmentStrategy?: ShowOrHidePropWithValue;
  yearBuilt?: ShowOrHidePropWithValue;
  portfolio?: KeyValueEntity[];
  category?: ProjectCategoryEnum;
  customCategory?: string;
  entityType?: 'Project';
  unpublishedId?: string;
  completeDate?: Date;
  updatedAt?: Date;
  clientId?: string;
  investmentDetails?: InvestmentGraphql[];
  distributions?: ProjectProperty[];
  calculationType?: CalculationTypeEnum;
  inceptionDate?: Date;
  closingDate?: Date;
  projectCancellationDate?: Date;
  kpis?: string[];
  investmentClasses?: investmentClassesType[];
}

//Offerings page data set
export interface Offering extends EntityStatus {
  id?: string; //unique offering id
  name?: string; //Name of the offer
  operatorId: string; //Related operator id
  description?: MultiLanguage; //Description of the offer
  location?: Address; //Address of the offer
  //not in use yet
  // currency?: string; //Base currency for the offer
  operator?: Operator; //related operator
  offerStartDate?: Date; //start date for the offer
  // not in use yet
  // offerPublishDate?: Date; // when the operator has published the offer
  duration?: string; //Investment expected duration
  strategy?: defaultStrategies | string; //strategy type of the investment
  kpis?: OfferKpiObject[]; //up to 4 kpi for the investment
  // not in use yet
  // viewMode?: viewModeEnum; // Type of viewing based on accreditation status
  category?: ProjectCategoryEnum; // category for the offer
  customCategory?: string; // custom category if it is a new one
  audience?: User[]; //array of audience for getting the offering publication
  entityType?: 'Offering'; //set the entity type
}

export class LayoutData {
  key?: LayoutEnum;
  value?: string[];
}

//Holds the type of view mode according to investor's accreditation status
export enum viewModeEnum {
  RESTRICTED = 'restricted',
  OPEN = 'open',
}

export enum LayoutEnum {
  updatesDetails = 'updatesDetails',
  titles = 'titles',
}

//create a key for the smart documents files
export const smartDocumentIdKey: string = 'smartDocuments';

export interface UpdateDetails {
  ref?: UpdateRef;
  title?: MultiLanguage;
  description?: MultiLanguage;
  progress?: number;
  reportDate?: Date;
  // documents?: DocumentFile[];
  files?: DraftFile[];
  id?: string;
  tag?: string;
  clientId?: string;
  customRequestFunction?: () => any;
  displayTimeline?: boolean;
}

export interface ProjectProperty {
  key: ProjectPropertyEnum;
  value: ProjectPropertyData[];
  series1Legend: MultiLanguage; //customized legend for the first series
  series2Legend: MultiLanguage; //customized legend for the second series
}

export enum ProjectPropertyEnum {
  distribution = 'distribution',
  occupancy = 'occupancy',
  rent_full_occupancy = 'rent_full_occupancy',
  rent = 'rent',
  rent_growth = 'rent_growth',
  assets = 'assets',
  loans = 'loans',
  roi = 'roi',
  noi = 'noi',
  roe = 'roe',
  coc = 'coc',
  ltc = 'ltc',
}
export class ProjectPropertyData {
  date?: Date | string;
  value?: number | string;
  title?: string;
  projected?: number | string;
  annotation?: string;
  id?: string;
}

interface Unpublished<T> extends T {
  unpublished: boolean;
  publishedId?: string;
}

export interface InvestmentUnpublished extends Investment {
  unpublished: boolean;
  publishedId?: string;
  deleted?: boolean;
}

export interface ProjectUnpublished extends Project {
  unpublished?: boolean;
  entityType?: 'ProjectUnpublished';
  publishAt?: Date;
  deleted?: boolean;
}

export interface FundUnpublished extends Fund {
  unpublished?: boolean;
  entityType?: 'FundUnpublished';
  publishAt?: Date;
  deleted?: boolean;
}

export interface FundWithAssetsLngLat extends Fund {
  projects?: ProjectWithLatLng[];
}

export interface KeyValueEntity {
  key: string;
  value: string | number | Date | boolean;
  group?: string;
}

export enum ProjectCategoryEnum {
  industrial = 'industrial',
  office_buildings = 'office_buildings',
  raw_land = 'raw_land',
  residential = 'residential',
  commercial = 'commercial',
  development = 'development',
  custom = 'custom',
}

export enum ProfCalculationTypeEnum {
  NET_IRR = 'netIrr',
  NET_ROI = 'netRoi',
  NET_COC = 'netCoc',
  GROSS_IRR = 'grossIrr',
  GROSS_ROI = 'grossRoi',
  GROSS_COC = 'grossCoc',
  ROI = 'netRoi',
}

//Holds profimex overview data fetch result from server
export enum ProfimexOverviewDataResult {
  IGNORE = 'ignore', //not profimex user or more than one operator
  PENDING = 'pending', //data is still pending from server
  RECEIVED = 'received', //data was received
}

/**
 * Holds profimex Id
 */
export const PROFIMEX_ID: string = 'b02c1370-4b9d-11ec-b7b9-a7995e4ae954';

export enum CalculationTypeEnum {
  IRR = 'IRR',
  XIRR = 'XIRR',
  // ROE = 'ROE',
  ROI = 'ROI',
  NET_IRR = 'NET_IRR',
  NET_ROI = 'NET_ROI',
  NET_COC = 'NET_COC',
  GROSS_IRR = 'GROSS_IRR',
  GROSS_ROI = 'GROSS_ROI',
  GROSS_COC = 'GROSS_COC',
}

export type Entity =
  | Fund
  | Project
  | ProjectUnpublished
  | FundUnpublished
  | Offering
  | OfferingUnpublished;

// defines the type of possible entities
export enum EntityType {
  FUND = 'Fund',
  PROJECT = 'Project',
  OFFERING = 'Offering',
  OPERATOR = 'Operator',
}

export type EntityUnpublished = ProjectUnpublished | FundUnpublished | OfferingUnpublished;

export interface SavingError {
  errorCode: number;
  description: string;
  data?: any;
  err?: any;
}

export interface PublishDto {
  operator?: OperatorUnpublished;
  entities?: EntityUnpublished[];
  entitiesToDelete?: EntityUnpublished[];
  investments?: InvestmentUnpublished[];
  investmentsToDelete?: InvestmentUnpublished[];
  investors?: InvestorUnpublished[];
  investorsToDelete?: InvestorUnpublished[];
  notifications?: (EntityUnpublished | OperatorUnpublished)[];
}

export type NavigationItemType = {
  data: any;
  label: string;
  to: string;
  content?: any[];
  icon?: string;
  hoverIcon?: string;
  iconWidth?: string;
  iconHeight?: string;
  onClick?: Function;
  badge?: JSX.Element;
  badgeRight?: string;
  extraSearchFields?: string[];
};

export type ScrollToLinkType = {
  label: string;
  ref: MutableRefObject;
};

export interface User extends DateVersion {
  email: string;
  role?: UserRolesEnum;
  settings?: UserSettings;
  firstName?: string;
  lastName?: string;
  imgUrl?: string;
  language?: string;
  // documents?: DocumentFile[];
  operatorDetails?: Operator[];
  investmentDetails?: Investment[];
}

export enum UserRolesEnum {
  admin = 'admin',
  operator = 'operator',
  investor = 'investor',
  visitor = 'visitor',
}

export interface UserSettings {
  use_mfa?: boolean;
  terms?: boolean;
  marketing?: boolean;
}

export enum DataScope {
  DRAFT = 'DRAFT',
  PUBLISH = 'PUBLISH',
}

export enum DataStatus {
  DELETED = 'DELETED', //deleted after being pending delete and published
  ACTIVE = 'ACTIVE',
  PENDING_DELETE = 'PENDING_DELETE', //being published once and then deleted from the operator side only
}

interface dateObject {
  $date: string;
}
// export interface DocumentFile {
//   createdAt: dateObject;
//   fileName: string; // New file
//   content: string; // New file
//   url: string; // New file
//   downloadUrl: string;
//   thumbnail?: string; // New file
//   public?: boolean; // New file
//   status?: DocAccessEnum; // New file
//   scope?: DataScope; // New file
//   shared?: boolean; // New file
//   size?: Number;
//   operatorId: string; // New file
//   projectId?: string; // New file
//   fundId?: string; // New file
//   offerId?: string; // New file
//   tags?: Tag[]; // New File
//   id: string;
// }

export type ArrangedDocumentFile = DraftFile[];

export enum DocAccessEnum {
  PRIVATE = 'PRIVATE',
  PUBLIC = 'PUBLIC',
  DRAFT = 'DRAFT',
  DELETED = 'PENDING_DELETE',
}

export class DocumentRef {
  key: DocRefEnum;
  value: string;
}

export enum DocRefEnum {
  updateId = 'updateId',
  investorId = 'investorId',
  operatorId = 'operatorId',
  projectId = 'projectId',
  fundId = 'fundId',
  offerId = 'offerId',
}

export type Tag = {
  type: FileTagsTypesEnum;
  extra?: string;
  value?: FileTagsValues | string; // Or string that the user typed
  hidden?: boolean;
};

/**
 * Defines some of the constant file tags values
 */
export enum FileTagsValues {
  UNTAGGED = 'UNTAGGED',
  K1 = 'K1',
  MAIN = 'MAIN',
  FUND_PORTFOLIO_MAIN = 'FUND_PORTFOLIO_MAIN',
  GALLERY = 'GALLERY',
  DOCUMENTS = 'DOCUMENTS',
  OPERATOR_PROFILE = 'OPERATOR_PROFILE',
  W8 = 'W8',
  W9 = 'W9',
  SIDE_LETTER = 'Side Letter',
  CRS = 'CRS',
  LPA = 'LPA',
  SUBSCRIPTION = 'Subscription',
  KYC = 'KYC',
  COMMITMENT_UPDATE = 'Commitment Update',
  INVESTOR_TAX = 'INVESTOR_TAX',
}

export enum FileTagsTypesEnum {
  INVESTOR = 'INVESTOR',
  UPDATE = 'UPDATE',
  AREA = 'AREA',
  CUSTOM = 'CUSTOM',
  TAX = 'TAX',
  VIEWER = 'VIEWER',
}

export type FileTagsTypes =
  | FileTagEnum.Investor
  | FileTagEnum.Viewer
  | FileTagEnum.Custom
  | FileTagsTypesEnum.TAX
  | FileTagsTypesEnum.AREA
  | FileTagEnum.Update;

export type FreeTextType = {
  value: string;
};

export enum FileContextEnum {
  OPERATOR = 'operator',
  OPERATOR_MAIN = 'operatorMain',
  OPERATOR_UPDATES = 'operatorUpdates',
  PROJECT = 'project',
  PROJECT_MAIN = 'projectMain',
  PROJECT_UPDATES = 'projectUpdates',
  PROJECT_DOCUMENTS = 'projectDocuments',
  PROJECT_GALLERY = 'projectGallery',
  FUND = 'fund',
  FUND_MAIN = 'fundMain',
  FUND_UPDATES = 'fundUpdates',
  FUND_PORTFOLIO = 'fundPortfolio',
  FUND_PORTFOLIO_MAIN = 'fundPortfolioMain',
  FUND_PORTFOLIO_DOCUMENTS = 'fundPortfolioDocuments',
  FUND_PORTFOLIO_GALLERY = 'fundPortfolioGallery',
  OFFERING = 'offering',
  OFFERING_DOCUMENTS = 'offeringDocuments',
  OFFERING_GALLERY = 'offeringGallery',
  INVESTOR = 'investorPersonal',
  INVESTOR_TAX = 'investorTax',
  INVESTOR_ACCREDITATION = 'investorAccreditation',
  INVESTOR_BANK = 'investorBank',
  FILE_TAGGING = 'fileTagging',
}

export enum FileParameterKeyEnum {
  OPERATOR = 'operatorId',
  PROJECT = 'projectId',
  FUND = 'fundId',
  PORTFOLIO = 'portfolioId',
  OFFER = 'offeringId',
  TAGS = 'tags',
}

export interface FileParameter {
  operatorId: string;
  projectId?: string;
  fundId?: string;
  portfolioId?: string;
  offeringId?: string;
  // updateId?: string,
  // area?: 'MAIN' | 'DOCUMENTS' | 'GALLERY' | 'ALL',
  tags?: FileTags[];
}

// holds project/fund entity and files to publish structure
export interface PublishFiles {
  entity?: any; //Fund or Project
  files?: FileParameter[];
}

export interface FileParameter {
  operatorId: string;
  projectId?: string;
  fundId?: string;
  portfolioId?: string;
  offeringId?: string;
  // updateId?: string,
  // area?: 'MAIN' | 'DOCUMENTS' | 'GALLERY' | 'ALL',
  tags?: Tag[];
}
export interface UploadFileParameter extends FileParameter {
  tagsProcess: boolean;
}

export interface DateVersion {
  id?: string;
  startDate?: Date;
  endDate?: Date;
}

export interface EntityStatus extends DateVersion {
  status?: EntityStatusEnum;
}

//Status for the entity
export enum EntityStatusEnum {
  PENDING = 'pending',
  STARTED = 'started',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum EntityPublishEnum {
  publish = 'publish',
  unpublish = 'unpublish',
}

export enum StatusCategoryFilterEnum {
  ALL = 'ALL',
  PENDING = 'PENDING',
  STARTED = 'STARTED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export type Role = 'investor' | 'operator' | 'admin';

export interface UnpublishedChange {
  diff: UnpublishedDiff[];
  id: string;
  refKey?: string;
  refValue?: string;
  key: UserDiffEnum;
  description?: string;
}

export interface UnpublishedDiff {
  kind: 'N' | 'U' | 'D';
  rhs?: any;
  lhs?: any;
}

export enum UserDiffEnum {
  operator = 'operator',
  operatorUpdate = 'operatorUpdate',
  fund = 'fund',
  fundUpdate = 'fundUpdate',
  fundProject = 'fundProject',
  fundInvestment = 'fundInvestment',
  operatorProject = 'operatorProject',
  projectUpdate = 'projectUpdate',
  projectInvestment = 'projectInvestment',
}

export type ProjectPublishSendDto = {
  id: string;
  key: UserDiffEnum;
};

export type FileToPublish = {
  id: string;
  scope: DataScope;
  status: DataStatus;
};

export type InvestorPublishSendDto = {
  operatorId: string;
  files: FileToPublish[];
};

export type AdminUserType = UpdateOperatorAdminInput & {
  fullName?: string;
};
